import { supabase } from './supabase';
import type { ExportConfig, ExportOutputs, ExportRecord } from '../types/export';
import {
  getExportStoragePath,
  getBackgroundFilename,
  getOverlayFilename,
  getCompositeFilename,
  EXPORT_FORMAT_MIME,
} from '../types/export';

const BUCKET_NAME = 'cs-exports';

export interface UploadResult {
  backgroundUrl?: string;
  overlayUrl?: string;
  compositeUrl?: string;
}

export async function uploadExportFiles(
  projectId: string,
  exportId: string,
  outputs: ExportOutputs,
  config: ExportConfig
): Promise<UploadResult> {
  const result: UploadResult = {};

  if (outputs.backgroundBlob && config.includeBackground) {
    const filename = getBackgroundFilename(config.format, config.scale);
    const path = getExportStoragePath(projectId, exportId, filename);

    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(path, outputs.backgroundBlob, {
        contentType: EXPORT_FORMAT_MIME[config.format],
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error('[ExportStorage] Failed to upload background:', error);
    } else {
      const { data: urlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(path);

      result.backgroundUrl = urlData.publicUrl;
    }
  }

  if (outputs.overlayBlob && config.includeOverlay) {
    const filename = getOverlayFilename();
    const path = getExportStoragePath(projectId, exportId, filename);

    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(path, outputs.overlayBlob, {
        contentType: 'text/plain',
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error('[ExportStorage] Failed to upload overlay:', error);
    } else {
      const { data: urlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(path);

      result.overlayUrl = urlData.publicUrl;
    }
  }

  if (outputs.compositeBlob && config.includeComposite) {
    const filename = getCompositeFilename(config.format, config.scale);
    const path = getExportStoragePath(projectId, exportId, filename);

    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(path, outputs.compositeBlob, {
        contentType: EXPORT_FORMAT_MIME[config.format],
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error('[ExportStorage] Failed to upload composite:', error);
    } else {
      const { data: urlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(path);

      result.compositeUrl = urlData.publicUrl;
    }
  }

  return result;
}

export async function getSignedUrls(
  projectId: string,
  exportId: string,
  format: ExportConfig['format'],
  scale: number
): Promise<{ backgroundUrl?: string; overlayUrl?: string }> {
  const result: { backgroundUrl?: string; overlayUrl?: string } = {};
  const expiresIn = 3600;

  const bgPath = getExportStoragePath(
    projectId,
    exportId,
    getBackgroundFilename(format, scale)
  );
  const overlayPath = getExportStoragePath(
    projectId,
    exportId,
    getOverlayFilename()
  );

  const { data: bgData } = await supabase.storage
    .from(BUCKET_NAME)
    .createSignedUrl(bgPath, expiresIn);

  if (bgData?.signedUrl) {
    result.backgroundUrl = bgData.signedUrl;
  }

  const { data: overlayData } = await supabase.storage
    .from(BUCKET_NAME)
    .createSignedUrl(overlayPath, expiresIn);

  if (overlayData?.signedUrl) {
    result.overlayUrl = overlayData.signedUrl;
  }

  return result;
}

export async function saveExportRecord(
  record: Omit<ExportRecord, 'created_by' | 'created_at'>
): Promise<ExportRecord | null> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;

  if (!userId) {
    console.error('[ExportStorage] No authenticated user for export record');
    return null;
  }

  const { data, error } = await supabase
    .from('cs_export_records')
    .insert({
      ...record,
      created_by: userId,
    })
    .select()
    .single();

  if (error) {
    console.error('[ExportStorage] Failed to save export record:', error);
    return null;
  }

  return data as ExportRecord;
}

export async function fetchExportHistory(
  projectId: string,
  limit = 20
): Promise<ExportRecord[]> {
  const { data, error } = await supabase
    .from('cs_export_records')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[ExportStorage] Failed to fetch export history:', error);
    return [];
  }

  return (data || []) as ExportRecord[];
}

export async function fetchExportById(exportId: string): Promise<ExportRecord | null> {
  const { data, error } = await supabase
    .from('cs_export_records')
    .select('*')
    .eq('id', exportId)
    .maybeSingle();

  if (error) {
    console.error('[ExportStorage] Failed to fetch export:', error);
    return null;
  }

  return data as ExportRecord | null;
}

export async function deleteExport(
  exportId: string,
  projectId: string,
  format: ExportConfig['format'],
  scale: number
): Promise<boolean> {
  const bgPath = getExportStoragePath(
    projectId,
    exportId,
    getBackgroundFilename(format, scale)
  );
  const overlayPath = getExportStoragePath(
    projectId,
    exportId,
    getOverlayFilename()
  );

  await supabase.storage.from(BUCKET_NAME).remove([bgPath, overlayPath]);

  const { error } = await supabase
    .from('cs_export_records')
    .delete()
    .eq('id', exportId);

  if (error) {
    console.error('[ExportStorage] Failed to delete export record:', error);
    return false;
  }

  return true;
}
